/**
 * 测试用例编辑器组件
 * 
 * 用于编辑测试用例的输入值和期望输出值
 */

import React, { useCallback } from 'react';
import type { TestCase, PlcVariable, TestValue } from '../../types/testTypes';
import { validateVariableValue } from '../../services/plcCodeService';

// ============================================
// 类型定义
// ============================================

export interface TestCaseEditorProps {
  /** 当前测试用例 */
  readonly testCase: TestCase;
  /** 输入变量定义 */
  readonly inputs: readonly PlcVariable[];
  /** 输出变量定义 */
  readonly outputs: readonly PlcVariable[];
  /** 是否正在运行 */
  readonly isRunning: boolean;
  /** 更新名称 */
  readonly onUpdateName: (name: string) => void;
  /** 更新描述 */
  readonly onUpdateDescription: (description: string) => void;
  /** 更新输入值 */
  readonly onUpdateInput: (variableName: string, value: string) => void;
  /** 更新期望输出 */
  readonly onUpdateOutput: (variableName: string, value: string) => void;
  /** 运行测试 */
  readonly onRun: () => void;
}

// ============================================
// 子组件：变量值输入行
// ============================================

interface VariableInputRowProps {
  readonly variable: PlcVariable;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled: boolean;
  readonly placeholder?: string;
}

const VariableInputRow: React.FC<VariableInputRowProps> = ({
  variable,
  value,
  onChange,
  disabled,
  placeholder
}) => {
  const isValid = !value || validateVariableValue(value, variable.dataType);
  
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      onChange(e.target.value);
    },
    [onChange]
  );
  
  // 布尔类型使用下拉选择
  if (variable.dataType === 'BOOL') {
    return (
      <tr className="plc-test-var-row">
        <td className="plc-test-var-name" title={variable.comment}>
          {variable.name}
          {variable.comment && (
            <span className="plc-test-var-comment">({variable.comment})</span>
          )}
        </td>
        <td className="plc-test-var-type">{variable.dataType}</td>
        <td className="plc-test-var-value">
          <select
            className="plc-test-input plc-test-select"
            value={value || 'FALSE'}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          >
            <option value="FALSE">FALSE</option>
            <option value="TRUE">TRUE</option>
          </select>
        </td>
      </tr>
    );
  }
  
  return (
    <tr className="plc-test-var-row">
      <td className="plc-test-var-name" title={variable.comment}>
        {variable.name}
        {variable.comment && (
          <span className="plc-test-var-comment">({variable.comment})</span>
        )}
      </td>
      <td className="plc-test-var-type">{variable.dataType}</td>
      <td className="plc-test-var-value">
        <input
          type="text"
          className={`plc-test-input ${!isValid ? 'invalid' : ''}`}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder ?? variable.initialValue ?? ''}
        />
        {!isValid && (
          <span className="plc-test-input-error">
            值不符合 {variable.dataType} 类型
          </span>
        )}
      </td>
    </tr>
  );
};

// ============================================
// 子组件：变量表格
// ============================================

interface VariableTableProps {
  readonly title: string;
  readonly icon: string;
  readonly variables: readonly PlcVariable[];
  readonly values: readonly TestValue[];
  readonly onChange: (variableName: string, value: string) => void;
  readonly disabled: boolean;
  readonly emptyText: string;
  readonly placeholder?: string;
}

const VariableTable: React.FC<VariableTableProps> = ({
  title,
  icon,
  variables,
  values,
  onChange,
  disabled,
  emptyText,
  placeholder
}) => {
  // 构建值映射
  const valueMap = new Map(values.map(v => [v.variableName, v.value]));
  
  if (variables.length === 0) {
    return (
      <div className="plc-test-section">
        <div className="plc-test-section-header">
          <i className={`fa fa-${icon}`} />
          <span>{title}</span>
        </div>
        <div className="plc-test-empty-section">
          <span>{emptyText}</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="plc-test-section">
      <div className="plc-test-section-header">
        <i className={`fa fa-${icon}`} />
        <span>{title} ({variables.length})</span>
      </div>
      <table className="plc-test-table">
        <thead>
          <tr>
            <th>变量名</th>
            <th>类型</th>
            <th>值</th>
          </tr>
        </thead>
        <tbody>
          {variables.map((variable) => (
            <VariableInputRow
              key={variable.name}
              variable={variable}
              value={valueMap.get(variable.name) ?? ''}
              onChange={(value) => onChange(variable.name, value)}
              disabled={disabled}
              placeholder={placeholder}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================
// 主组件：测试用例编辑器
// ============================================

export const TestCaseEditor: React.FC<TestCaseEditorProps> = ({
  testCase,
  inputs,
  outputs,
  isRunning,
  onUpdateName,
  onUpdateDescription,
  onUpdateInput,
  onUpdateOutput,
  onRun
}) => {
  return (
    <div className="plc-test-editor">
      {/* 用例基本信息 */}
      <div className="plc-test-editor-header">
        <div className="plc-test-editor-info">
          <input
            type="text"
            className="plc-test-input plc-test-name-input"
            value={testCase.name}
            onChange={(e) => onUpdateName(e.target.value)}
            placeholder="测试用例名称"
            disabled={isRunning}
          />
          <input
            type="text"
            className="plc-test-input plc-test-desc-input"
            value={testCase.description ?? ''}
            onChange={(e) => onUpdateDescription(e.target.value)}
            placeholder="描述（可选）"
            disabled={isRunning}
          />
        </div>
        
        <button
          type="button"
          className="plc-test-btn plc-test-btn-primary"
          onClick={onRun}
          disabled={isRunning}
        >
          <i className={`fa fa-${isRunning ? 'spinner fa-spin' : 'play'}`} />
          <span>{isRunning ? '运行中' : '运行'}</span>
        </button>
      </div>
      
      {/* 输入变量 */}
      <VariableTable
        title="输入变量"
        icon="sign-in"
        variables={inputs}
        values={testCase.inputs}
        onChange={onUpdateInput}
        disabled={isRunning}
        emptyText="无输入变量"
      />
      
      {/* 期望输出 */}
      <VariableTable
        title="期望输出"
        icon="sign-out"
        variables={outputs}
        values={testCase.expectedOutputs}
        onChange={onUpdateOutput}
        disabled={isRunning}
        emptyText="无输出变量"
        placeholder="输入期望值"
      />
    </div>
  );
};

export default TestCaseEditor;